// src/pages/messages/NewDmPage.jsx
//
// Chat v2 New Message page (route: /messages/new). Two compose
// paths in one place:
//
//   1. Post to a Team — go to a team_announcement channel where
//      I'm an admin. Coaches use this to message their roster.
//      Hidden when the user has no team channels they can post in.
//
//   2. Direct Message — start (or open) a 1:1 with a teammate.
//      Lists everyone I share at least one team channel with.
//      Coaches first (with "COACH" pill), then parents
//      alphabetical. Click → start/open DM → navigate.
//
// Picker source for #2: useDmCandidates filters from team
// channels I'm a participant in.

import React, { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '@/context/AuthContext'
import useDmCandidates from '@/hooks/useDmCandidates'
import useChats from '@/hooks/useChats'
import { startOrOpenDm } from '@/services/chatStartDm'
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  ChevronRightIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

function CandidateAvatar({ candidate }) {
  if (candidate.photoURL) {
    return (
      <img
        src={candidate.photoURL}
        alt=""
        className="h-11 w-11 rounded-2xl object-cover ring-1 ring-zinc-200 flex-shrink-0"
      />
    )
  }
  return (
    <div className="h-11 w-11 rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center flex-shrink-0">
      <span className="text-sm font-bold text-[#5a8a1f] uppercase">
        {candidate.displayName?.[0] || '?'}
      </span>
    </div>
  )
}

// Avatar for a team channel — same shape as the DM avatars but
// with the team logo (or megaphone fallback in lime).
function TeamChannelAvatar({ chat }) {
  if (chat.photoUrl) {
    return (
      <img
        src={chat.photoUrl}
        alt=""
        className="h-11 w-11 rounded-2xl object-cover ring-1 ring-zinc-200 flex-shrink-0"
      />
    )
  }
  return (
    <div className="h-11 w-11 rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center flex-shrink-0">
      <MegaphoneIcon className="h-5 w-5 text-[#5a8a1f]" />
    </div>
  )
}

function CandidateRow({ candidate, onPick, isStarting }) {
  return (
    <button
      type="button"
      onClick={() => onPick(candidate)}
      disabled={isStarting}
      className="w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-[#f7faf5] transition-colors flex items-center gap-3 disabled:opacity-60"
    >
      <CandidateAvatar candidate={candidate} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-[#0d1f14] truncate">
            {candidate.displayName}
          </p>
          {candidate.isCoach && (
            <span
              className="inline-flex items-center gap-0.5 rounded bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/30 text-[#5a8a1f] px-1.5 py-px text-[9px] font-bold uppercase"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              Coach
            </span>
          )}
          {candidate.existingDmId && (
            <span className="text-[10px] text-zinc-400 italic">existing chat</span>
          )}
        </div>
        <p className="text-[11px] text-zinc-500">
          {candidate.sharedTeamIds.length > 1
            ? `Shared in ${candidate.sharedTeamIds.length} teams`
            : 'Shared team'}
        </p>
      </div>

      {isStarting
        ? <ArrowPathIcon className="h-4 w-4 text-zinc-400 animate-spin flex-shrink-0" />
        : <ChevronRightIcon className="h-4 w-4 text-zinc-300 flex-shrink-0" />}
    </button>
  )
}

function TeamChannelRow({ chat, currentUid }) {
  const memberCount = (chat.participantIds || []).length
  return (
    <Link
      to={`/messages/${chat.id}`}
      className="block px-4 py-3 border-b border-zinc-100 hover:bg-[#f7faf5] transition-colors"
    >
      <div className="flex items-center gap-3">
        <TeamChannelAvatar chat={chat} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-[#0d1f14] truncate">
              {chat.name || 'Team'}
            </p>
            <span
              className="inline-flex items-center gap-0.5 rounded bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/30 text-[#5a8a1f] px-1.5 py-px text-[9px] font-bold uppercase"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              Team
            </span>
          </div>
          <p className="text-[11px] text-zinc-500">
            Post an announcement · {memberCount} {memberCount === 1 ? 'member' : 'members'}
          </p>
        </div>
        <ChevronRightIcon className="h-4 w-4 text-zinc-300 flex-shrink-0" />
      </div>
    </Link>
  )
}

function EmptyState({ hasSearch }) {
  return (
    <div className="text-center py-16 px-6">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 mb-5">
        <UserIcon className="h-8 w-8 text-[#5a8a1f]" />
      </div>
      <h3
        className="text-xl font-bold uppercase text-[#0d1f14] mb-2"
        style={{ fontFamily: HEAD_FONT }}
      >
        {hasSearch ? 'No matches' : 'No teammates yet'}
      </h3>
      <p className="text-sm text-zinc-500 max-w-sm mx-auto">
        {hasSearch
          ? 'Try a different name.'
          : 'You can DM coaches and parents you share a team with. Invite parents to register, or join a club to see more people here.'}
      </p>
    </div>
  )
}

function SectionHeader({ children }) {
  return (
    <p
      className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-2 mt-4 px-1 first:mt-0"
      style={{ fontFamily: HEAD_FONT }}
    >
      {children}
    </p>
  )
}

export default function NewDmPage() {
  const navigate = useNavigate()
  const { firebaseUser, userProfile } = useAuth()
  const uid = firebaseUser?.uid || null
  const { candidates, loading: candidatesLoading } = useDmCandidates(uid)
  const { chats, loading: chatsLoading } = useChats(uid)

  const [search, setSearch] = useState('')
  const [startingUid, setStartingUid] = useState(null)

  // Team channels where I can POST (admin role only). For
  // parents (read-only members) the team channel still appears
  // in the sidebar inbox, but doesn't make sense in the "compose
  // new message" picker — they can't post in it.
  const postableTeamChannels = useMemo(() => {
    if (!uid) return []
    return chats.filter((c) => {
      if (c.type !== 'team_announcement') return false
      return c.members?.[uid]?.role === 'admin'
    })
  }, [chats, uid])

  // Filter both lists by the same search term
  const q = search.trim().toLowerCase()
  const filteredTeams = useMemo(() => {
    if (!q) return postableTeamChannels
    return postableTeamChannels.filter((c) => (c.name || '').toLowerCase().includes(q))
  }, [postableTeamChannels, q])
  const filteredCandidates = useMemo(() => {
    if (!q) return candidates
    return candidates.filter((c) => c.displayName.toLowerCase().includes(q))
  }, [candidates, q])

  const myDisplayName = useMemo(() => (
    userProfile?.displayName ||
    [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ').trim() ||
    firebaseUser?.displayName ||
    (firebaseUser?.email ? firebaseUser.email.split('@')[0] : 'You')
  ), [userProfile, firebaseUser])
  const myPhotoURL = userProfile?.photoURL || firebaseUser?.photoURL || null

  const handlePick = async (candidate) => {
    if (candidate.existingDmId) {
      navigate(`/messages/${candidate.existingDmId}`)
      return
    }
    if (!uid) return
    setStartingUid(candidate.uid)
    try {
      const chatId = await startOrOpenDm({
        me: { uid, displayName: myDisplayName, photoURL: myPhotoURL },
        other: { uid: candidate.uid, displayName: candidate.displayName, photoURL: candidate.photoURL },
      })
      navigate(`/messages/${chatId}`)
    } catch (err) {
      console.error('[NewDmPage] start DM failed:', err?.code, err?.message)
      toast.error(err?.message || 'Could not start chat. Try again.')
      setStartingUid(null)
    }
  }

  const loading = candidatesLoading || chatsLoading
  const totalVisible = filteredTeams.length + filteredCandidates.length

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">

        {/* Back + header */}
        <Link
          to="/messages"
          className="inline-flex items-center gap-1.5 text-sm text-[#5a8a1f] hover:text-[#3d6010] mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Messages
        </Link>

        <div className="mb-5">
          <h1
            className="text-2xl font-bold uppercase tracking-tight text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            New Message
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Post to a team channel, or start a direct conversation with one person.
          </p>
        </div>

        {/* Search */}
        {(candidates.length > 0 || postableTeamChannels.length > 0) && (
          <div className="relative mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search teams or people..."
              className="w-full rounded-xl border border-zinc-200 bg-white pl-9 pr-4 py-2.5 text-sm text-[#0d1f14] placeholder:text-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
            />
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-400">
            <ArrowPathIcon className="h-6 w-6 animate-spin" />
          </div>
        ) : totalVisible === 0 ? (
          <EmptyState hasSearch={q.length > 0} />
        ) : (
          <div>
            {filteredTeams.length > 0 && (
              <>
                <SectionHeader>Post to a Team</SectionHeader>
                <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
                  {filteredTeams.map((c) => (
                    <TeamChannelRow key={c.id} chat={c} currentUid={uid} />
                  ))}
                </div>
              </>
            )}

            {filteredCandidates.length > 0 && (
              <>
                <SectionHeader>Direct Message</SectionHeader>
                <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
                  {filteredCandidates.map((c) => (
                    <CandidateRow
                      key={c.uid}
                      candidate={c}
                      onPick={handlePick}
                      isStarting={startingUid === c.uid}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Tip when there are candidates */}
        {!loading && (candidates.length > 0 || postableTeamChannels.length > 0) && (
          <div className="mt-6 rounded-xl bg-white border border-zinc-200 px-4 py-3 flex items-start gap-3">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-[#5a8a1f] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-[#0d1f14]">
                Team channels vs Direct messages
              </p>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                Team channels reach everyone on your roster at once. DMs are private 1:1. If you've already chatted with someone, picking them again opens your existing thread.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
